const COMPOSE_URL_LIMIT = 1800;

const SUBJECTS = {
  iptal: "Abonelik İptal Talebi",
  fatura: "Fatura / Cayma Bedeli İtirazı",
  iade: "İade / Değişim Talebi",
  hakem: "Tüketici Hakem Heyeti Başvurusu Hazırlığı",
};

function normalizeIssueType(issueType) {
  const value = String(issueType || "").toLocaleLowerCase("tr-TR");
  if (value === "iptal" || value.includes("iptal") || value.includes("fesih")) return "iptal";
  if (value === "fatura" || value.includes("cayma") || value.includes("fatura")) return "fatura";
  if (value === "iade" || value.includes("değişim") || value.includes("iade")) return "iade";
  if (value === "hakem" || value.includes("hakem")) return "hakem";
  return null;
}

function safeHeaderValue(value) {
  return String(value || "").replace(/[\r\n]+/g, " ").trim().slice(0, 100);
}

export function getPreferredEmailProvider(senderEmail) {
  const domain = String(senderEmail || "").trim().toLocaleLowerCase("tr-TR").split("@")[1] || "";
  if (["gmail.com", "googlemail.com"].includes(domain)) return "gmail";
  if (["outlook.com", "hotmail.com", "live.com", "msn.com", "office365.com"].includes(domain)) {
    return "outlook";
  }
  return "mailto";
}

export function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export function normalizeEmailAddress(value) {
  return String(value || "").trim().toLowerCase();
}

export function isSameEmailAddress(firstAddress, secondAddress) {
  const first = normalizeEmailAddress(firstAddress);
  const second = normalizeEmailAddress(secondAddress);
  return Boolean(first && second && first === second);
}

export function createEmailSubject(issueType, institutionName, subscriptionNumber, options = {}) {
  const normalizedIssue = normalizeIssueType(issueType);
  const subject = SUBJECTS[normalizeIssueType(issueType)] || "Başvuru Talebi";
  const institution = safeHeaderValue(institutionName);
  const serviceNumber = safeHeaderValue(subscriptionNumber);
  if (normalizedIssue === "iptal" && institution) {
    return serviceNumber
      ? `${institution} Abonelik Fesih Talebi – Hizmet No: ${serviceNumber}`
      : `${institution} Abonelik Fesih Talebi`;
  }
  if (options.flow === "invoice") {
    return serviceNumber
      ? `${institution || "Kurum"} Fatura İtirazı – Hizmet No: ${serviceNumber}`
      : `${institution || "Kurum"} Fatura İtirazı`;
  }
  if (options.flow === "refund") {
    const transactionNumber = safeHeaderValue(options.transactionNumber);
    const commerceSubject = {
      cancel_before_shipping: "Sipariş İptal Talebi",
      return_product: "Ürün İade Talebi",
      defective_or_wrong_product: "Bozuk, Eksik veya Yanlış Ürün Talebi",
      refund_not_received: "Para İadesi Durum Talebi",
      return_rejected: "Reddedilen İade Talebi İtirazı",
      not_delivered: "Teslim Edilmeyen Sipariş Talebi",
      duplicate_charge: "Mükerrer Tahsilat İtirazı",
      other: "Sipariş Sorunu Talebi",
    }[options.commerceProblem] || "Para İadesi Talebi";
    return transactionNumber
      ? `${commerceSubject} – Sipariş No: ${transactionNumber}`
      : commerceSubject;
  }
  if (options.flow === "product-service") {
    return institution ? `${institution} Ürün / Hizmet Sorunu` : "Ürün / Hizmet Sorunu";
  }
  return institution ? `${subject} – ${institution}` : subject;
}

const CANCELLATION_SENTENCES = {
  "İlk kez iptal başvurusu yapacağım": "Bu başvuru ilk iptal talebimdir.",
  "Başvurdum ama iptal edilmedi": "Daha önce başvurmama rağmen iptal işlemi tamamlanmadı.",
  "Cayma bedeli çıkarılıyor": "Bildirilen cayma bedelinin dayanağının yazılı olarak açıklanmasını istiyorum.",
  "İptal ettim ama fatura gelmeye devam ediyor": "İptal talebime rağmen fatura veya ücret yansıtılmaya devam ediyor.",
  "Modem veya cihaz iadesi sorunum var": "Cihaz iadesinin yeri, yöntemi ve son tarihinin yazılı olarak bildirilmesini istiyorum.",
  "Taahhüdüm bitti ama abonelik sürüyor": "Taahhüt sürem sona ermesine rağmen abonelik devam ediyor.",
  "Kuruma ulaşamıyorum": "Doğrudan iletişim kanallarından sonuç alamadım.",
  "Başka bir sorun yaşıyorum": "İptal talebimle ilgili ek durumu aşağıda açıkladım.",
};

export function stripDigitalCancellationSignature(value) {
  return String(value || "")
    .replace(/^\s*(?:Islak\s+)?İmza\s*:?.*$/gim, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function stripDigitalSignature(value) {
  return String(value || "")
    .replace(/^\s*(?:Islak\s+)?İmza(?:\s+çizgisi)?\s*:?.*$/gim, "")
    .replace(/^\s*Kalemle imzalayınız.*$/gim, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function createCancellationEmailBody({
  company,
  problemLabel,
  description,
  applicant = {},
  subscriptionLabel,
  subscriptionNumber,
}) {
  const institution = String(company || "ilgili kurum").trim();
  const detail = CANCELLATION_SENTENCES[problemLabel]
    || "Aboneliğimin sonlandırılmasını istiyorum.";
  const customDescription = String(description || "").trim();
  const applicantLines = [
    ["Ad Soyad", [applicant.firstName, applicant.lastName].filter(Boolean).join(" ").trim()],
    ["E-posta", String(applicant.email || "").trim()],
    ["Telefon", String(applicant.phone || "").trim()],
    [subscriptionLabel || "Abone / Müşteri Numarası", String(subscriptionNumber || "").trim()],
  ].filter(([, value]) => value);

  const lines = [
    "Sayın Yetkili,",
    "",
    `${institution} nezdindeki aboneliğimin bu başvuru tarihi itibarıyla feshedilmesini talep ediyorum.`,
    detail,
    ...(customDescription ? [customDescription] : []),
    "",
    "İptalin geçerli olduğu tarihin ve işlemin tamamlandığının tarafıma yazılı olarak bildirilmesini rica ederim.",
    ...(problemLabel === "Modem veya cihaz iadesi sorunum var"
      ? ["Varsa teslim etmem gereken cihazlar için iade adımlarını da yazılı olarak iletmenizi rica ederim."]
      : []),
  ];

  if (applicantLines.length) {
    lines.push("", "Başvuru sahibi bilgileri:");
    applicantLines.forEach(([label, value]) => lines.push(`${label}: ${value}`));
  }
  return stripDigitalCancellationSignature(lines.join("\n"));
}

function appendApplicantBlock(lines, applicant, subscriptionLabel, subscriptionNumber, extraRows = []) {
  const rows = [
    ["Ad Soyad", [applicant?.firstName, applicant?.lastName].filter(Boolean).join(" ").trim()],
    ["E-posta", String(applicant?.email || "").trim()],
    ["Telefon", String(applicant?.phone || "").trim()],
    [subscriptionLabel || "Abone / Müşteri / Hizmet No", String(subscriptionNumber || "").trim()],
    ...extraRows,
  ].filter(([, value]) => String(value || "").trim());
  if (!rows.length) return;
  lines.push("", "Başvuru Sahibi Bilgileri");
  rows.forEach(([label, value]) => lines.push(`${label}: ${String(value).trim()}`));
}

export function createV1ApplicationEmailBody({
  flow,
  company,
  problemLabel,
  description,
  desiredOutcome,
  commerceProblem,
  info = {},
  applicant = {},
  subscriptionLabel,
  subscriptionNumber,
}) {
  const institution = String(company || "ilgili kurum").trim();
  const lines = ["Sayın Yetkili,", ""];

  if (flow === "invoice") {
    lines.push(
      `${institution} tarafından düzenlenen${info.billingPeriod ? ` ${info.billingPeriod} dönemine ait` : ""} faturaya itiraz ediyorum.`,
      problemLabel ? `İtiraz nedenim: ${problemLabel}.` : "Faturadaki ücretin incelenmesini istiyorum.",
      "İtiraz ettiğim ücretin nasıl hesaplandığının yazılı olarak açıklanmasını ve hatalıysa düzeltilmesini istiyorum.",
    );
    if (info.disputedAmount) lines.push(`İtiraz ettiğim tutar ${info.disputedAmount} olup tahsil edildiyse iadesini istiyorum.`);
    if (description) lines.push(description);
    lines.push("İnceleme sonucu ile yapılacak düzeltmenin tarafıma yazılı olarak bildirilmesini rica ederim.");
    appendApplicantBlock(lines, applicant, subscriptionLabel, subscriptionNumber, [
      ["Fatura dönemi", info.billingPeriod],
      ["Fatura tutarı", info.invoiceAmount],
      ["İtiraz edilen tutar", info.disputedAmount],
      ["Fatura / hesap numarası", info.invoiceNumber],
    ]);
  } else if (flow === "refund") {
    const commerceOpening = {
      cancel_before_shipping: `${institution} üzerinden verdiğim sipariş teslim edilmeden önce iptal işlemini başlatmak istiyorum.`,
      return_product: `${institution} üzerinden aldığım ürün için iade sürecini başlatmak istiyorum.`,
      defective_or_wrong_product: `${institution} üzerinden aldığım bozuk, eksik veya yanlış ürünle ilgili seçtiğim çözümün değerlendirilmesini istiyorum.`,
      refund_not_received: `${institution} üzerinden iade ettiğim ürünün geri ödeme durumunun incelenmesini istiyorum.`,
      return_rejected: `${institution} üzerindeki reddedilen iade talebimin yeniden değerlendirilmesini istiyorum.`,
      not_delivered: `${institution} üzerinden verdiğim ancak teslim edilmeyen siparişimin incelenmesini istiyorum.`,
      duplicate_charge: `${institution} siparişim için yapılan mükerrer tahsilatın incelenmesini istiyorum.`,
      unreachable: `${institution} veya ilgili satıcıya ulaşamadığım için asıl talebimin yazılı olarak değerlendirilmesini istiyorum.`,
      other: `${institution} üzerinden verdiğim siparişle ilgili sorunumun incelenmesini istiyorum.`,
    }[commerceProblem];
    lines.push(
      commerceOpening || `${institution} nezdindeki ödeme veya işlemim için para iadesi talep ediyorum.`,
      problemLabel ? `Talebimin nedeni: ${problemLabel}.` : "Talebimin incelenmesini istiyorum.",
    );
    if (info.transactionNumber) lines.push(`İşlem / sipariş numarası: ${info.transactionNumber}.`);
    if (info.paymentDate || info.paidAmount) {
      lines.push(`Ödeme bilgisi:${info.paymentDate ? ` ${info.paymentDate} tarihinde` : ""}${info.paidAmount ? ` ${info.paidAmount}` : ""}.`);
    }
    if (info.refundAmount) lines.push(`Talep ettiğim iade tutarı: ${info.refundAmount}.`);
    if (info.productName) lines.push(`Ürün: ${info.productName}.`);
    if (info.sellerName) lines.push(`Satıcı / mağaza: ${info.sellerName}.`);
    if (info.orderDate) lines.push(`Sipariş tarihi: ${info.orderDate}.`);
    if (info.refundRequestDate) lines.push(`İade talep tarihi: ${info.refundRequestDate}.`);
    if (info.returnShippingDate) lines.push(`Ürünün kargoya verildiği tarih: ${info.returnShippingDate}.`);
    if (info.returnShippingCode) lines.push(`İade kargo kodu: ${info.returnShippingCode}.`);
    if (info.trackingNumber) lines.push(`Kargo takip numarası: ${info.trackingNumber}.`);
    if (info.returnDeliveredDate) lines.push(`İade ürününün teslim tarihi: ${info.returnDeliveredDate}.`);
    if (info.statedRefundDate) lines.push(`İade edildiği belirtilen tarih: ${info.statedRefundDate}.`);
    if (info.paymentMethod) lines.push(`Ödeme yöntemi: ${info.paymentMethod}.`);
    if (info.platformResponse) lines.push(`Platformun veya satıcının cevabı: ${info.platformResponse}.`);
    if (commerceProblem === "defective_or_wrong_product" && desiredOutcome) lines.push(desiredOutcome);
    if (description) lines.push(description);
    const commerceClosing = commerceProblem === "cancel_before_shipping"
      ? "Sipariş iptali talebimin sonucunun tarafıma yazılı olarak bildirilmesini rica ederim."
      : commerceProblem === "defective_or_wrong_product"
        ? "Seçtiğim çözüm talebinin sonucunun tarafıma yazılı olarak bildirilmesini rica ederim."
        : commerceProblem === "not_delivered" || commerceProblem === "duplicate_charge" || commerceProblem === "other"
          ? "İnceleme sonucunun ve uygulanacak çözümün tarafıma yazılı olarak bildirilmesini rica ederim."
          : "İade talebimin sonucunun ve iadenin yapılacağı tarihin tarafıma yazılı olarak bildirilmesini rica ederim.";
    lines.push(commerceClosing);
    appendApplicantBlock(lines, applicant, subscriptionLabel, subscriptionNumber, commerceProblem ? [] : [
      ["İşlem / sipariş numarası", info.transactionNumber],
      ["Ödeme tarihi", info.paymentDate],
      ["Ödenen tutar", info.paidAmount],
      ["Talep edilen iade tutarı", info.refundAmount],
      ["Ürün", info.productName],
      ["Satıcı / mağaza", info.sellerName],
    ]);
  } else if (flow === "product-service") {
    lines.push(
      `${institution} üzerinden aldığım ürün veya hizmetle ilgili sorunun giderilmesini istiyorum.`,
      problemLabel ? `Yaşadığım sorun: ${problemLabel}.` : "Yaşadığım sorunun incelenmesini istiyorum.",
    );
    if (info.productName) lines.push(`Ürün / hizmet: ${info.productName}.`);
    if (info.faultDescription) lines.push(`Sorunun açıklaması: ${info.faultDescription}.`);
    if (desiredOutcome) lines.push(desiredOutcome);
    if (description) lines.push(description);
    lines.push("Talebimin değerlendirilerek sonucunun tarafıma yazılı olarak bildirilmesini rica ederim.");
    appendApplicantBlock(lines, applicant, subscriptionLabel, subscriptionNumber, [
      ["Ürün / hizmet", info.productName],
      ["Satın alma tarihi", info.purchaseDate],
      ["Sipariş / fatura numarası", info.transactionNumber],
      ["Ödenen tutar", info.paidAmount],
      ["Servis kayıt numarası", info.serviceNumber],
    ]);
  }

  return stripDigitalSignature(lines.join("\n"));
}

export function containsTurkishNationalId(text) {
  // Boşluk, nokta veya tireyle yazılmış 11 haneli değerleri de ihtiyatlı biçimde
  // kişisel veri kabul eder. Yanlış pozitif, URL'ye kişisel veri yazmaktan güvenlidir.
  return /(?:^|[^0-9])[1-9](?:[\s.-]?\d){10}(?!\d)/.test(String(text || ""));
}

export async function copyTextSafely(value) {
  const text = String(value ?? "");
  try {
    if (!navigator.clipboard?.writeText) throw new Error("clipboard_unavailable");
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    let textarea;
    try {
      textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.readOnly = true;
      textarea.setAttribute("aria-hidden", "true");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      textarea.style.pointerEvents = "none";
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      return document.execCommand?.("copy") === true;
    } catch {
      return false;
    } finally {
      textarea?.remove();
    }
  }
}

function buildProviderUrl(provider, recipient, subject, body) {
  if (provider === "mailto") {
    const params = new URLSearchParams({ subject });
    if (body !== undefined) params.set("body", body);
    return `mailto:${encodeURIComponent(recipient)}?${params.toString()}`;
  }

  const url = provider === "gmail"
    ? new URL("https://mail.google.com/mail/")
    : new URL("https://outlook.live.com/mail/0/deeplink/compose");

  if (provider === "gmail") {
    url.searchParams.set("view", "cm");
    url.searchParams.set("fs", "1");
    url.searchParams.set("to", recipient);
    url.searchParams.set("su", subject);
    if (body !== undefined) url.searchParams.set("body", body);
  } else {
    url.searchParams.set("to", recipient);
    url.searchParams.set("subject", subject);
    if (body !== undefined) url.searchParams.set("body", body);
  }
  return url.toString();
}

export function createComposeTarget({
  provider,
  recipient,
  subject,
  body,
  hasNationalId = false,
}) {
  if (!recipient || !["mailto", "gmail", "outlook"].includes(provider)) return null;
  const safeRecipient = safeHeaderValue(recipient);
  const safeSubject = safeHeaderValue(subject);
  const message = String(body || "");
  const bodyAllowed = !hasNationalId && !containsTurkishNationalId(message);
  const withoutBody = buildProviderUrl(provider, safeRecipient, safeSubject);
  const withBody = bodyAllowed
    ? buildProviderUrl(provider, safeRecipient, safeSubject, message)
    : withoutBody;
  const bodyIncluded = bodyAllowed && withBody.length <= COMPOSE_URL_LIMIT;

  return {
    url: bodyIncluded ? withBody : withoutBody,
    bodyIncluded,
    shouldCopyBody: !bodyIncluded && message.length > 0,
  };
}

export const NEW_WINDOW_RESULT = Object.freeze({
  OPENED: "opened",
  UNVERIFIED: "unverified",
  FAILED: "failed",
});

export function openSafeNewWindow(url) {
  try {
    if (new URL(url).protocol !== "https:") return NEW_WINDOW_RESULT.FAILED;
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) return NEW_WINDOW_RESULT.UNVERIFIED;
    try {
      opened.opener = null;
    } catch {
      // Bazı tarayıcılar açılan sekmenin WindowProxy nesnesini erişilemez kılar.
      // Sekme açılmış olduğu için bu durum açılış hatası değildir.
    }
    return NEW_WINDOW_RESULT.OPENED;
  } catch {
    return NEW_WINDOW_RESULT.FAILED;
  }
}
